import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { ApiResponse } from 'apisauce';

import { Button, Icon, RouterLink } from '@components/base';
import useReferencedByQuery from '@hooks/queryHooks/useReferencedByQuery';
import useDisableBlocker from '@hooks/useDisableBlocker';
import useSearchContext from '@hooks/useSearchContext';
import useSnack from '@hooks/useSnack';
import { LayoutEntityType } from '@models/Content';
import { getPagePath } from 'src/appRoutes';

type DeleteItemDialogProps = {
  deleteHandler: () => Promise<ApiResponse<unknown, unknown>>;
  entityId: string;
  entityType: LayoutEntityType;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
};

const ShiftedIcon = styled(Icon)`
  transform: ${({ theme }) => `translateY(${theme.spacing(0.25)})`};
`;

const DeleteItemDialog: FC<DeleteItemDialogProps> = ({
  deleteHandler,
  entityId,
  entityType,
  onClose,
  onSuccess,
}) => {
  const { addSnack } = useSnack();
  const { t } = useTranslation();
  const { lcSearchLanguage } = useSearchContext();
  const { setDisableBlocker } = useDisableBlocker();

  const [deleting, setDeleting] = useState(false);

  const {
    data: references = [],
    error,
    isLoading,
    refetch,
  } = useReferencedByQuery(entityType, entityId);

  const handleDelete = async () => {
    setDeleting(true);

    await refetch();

    if (references.length === 0)
      deleteHandler()
        .then(({ ok, originalError }) => {
          if (originalError) throw originalError;
          if (!ok) throw new Error();

          addSnack(
            t('deleteItem.success', {
              contentType: t(`contentType.${entityType}`),
            })
          );

          setDisableBlocker(true);
          onSuccess?.();
        })
        .catch(() =>
          addSnack(
            t('deleteItem.error', {
              contentType: t(`contentType.${entityType}`).toLocaleLowerCase(),
            }),
            { actions: { label: t('retry'), onClick: handleDelete }, variant: 'error' }
          )
        )
        .finally(() => setDeleting(false));
  };

  const contentTypeStr = t(`contentType.${entityType}`).toLocaleLowerCase();

  return (
    <Dialog fullWidth maxWidth='sm' onClose={deleting ? undefined : onClose} open>
      <DialogTitle>{t('deleteItem.title', { type: contentTypeStr })}</DialogTitle>

      <DialogContent>
        {isLoading && (!references || references.length === 0) ? (
          <Box alignItems='center' display='flex' justifyContent='center' p={4} width={1}>
            <CircularProgress color='inherit' />
          </Box>
        ) : error ? (
          <Alert severity='error'>
            <AlertTitle>{t('deleteItem.referenceError.alertTitle')}</AlertTitle>
            {t('deleteItem.referenceError.description', { type: contentTypeStr })}
          </Alert>
        ) : references.length === 0 ? (
          t('deleteItem.confirm', { type: contentTypeStr })
        ) : (
          <Fragment>
            {t('deleteItem.hasReferences.description', {
              count: references.length,
              type: contentTypeStr,
            })}

            <Box component='ul' mt={2}>
              {references.map(reference => (
                <li key={JSON.stringify(reference)}>
                  {reference.id ? (
                    <RouterLink
                      color='secondary'
                      gap={0.5}
                      target='_blank'
                      to={getPagePath(reference.id)}
                    >
                      {reference.name[lcSearchLanguage]}{' '}
                      <ShiftedIcon fontSize='inherit' name='openInNew' />
                    </RouterLink>
                  ) : (
                    <Typography>
                      {reference.name?.[lcSearchLanguage] ??
                        t('missingItem', { type: t('contentType.page') })}
                    </Typography>
                  )}
                </li>
              ))}
            </Box>
          </Fragment>
        )}
      </DialogContent>

      <DialogActions>
        <Button disabled={deleting} onClick={onClose}>
          {t('cancel')}
        </Button>

        <Button
          disabled={isLoading || references.length > 0}
          loading={deleting}
          onClick={handleDelete}
          variant='contained'
        >
          {t('confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteItemDialog;
